"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useCallback, useMemo } from "react";
import { BettingOdds as BettingOddsUI } from "fansunited-sports-ui";
import type { FUSportsBettingOperatorOdds } from "fansunited-data-layer";
import { useServerMatchOdds } from "../useServerMatchOdds";
import { toBookmakerOdds } from "../adapters";
import { translateOddsSelections } from "../translateOddsLabels";
import { useOddsDisplay } from "../useOddsDisplay";
import type { BookmakerOdds, MarketType, OddsData } from "../types";
import { useBetslip } from "@/features/betslip/useBetslip";
import type { BetslipMarket } from "@/features/betslip/types";
import { useLabels } from "@/lib/labels/LabelsProvider";
interface HeaderMarketOddsProps {
  matchId: string;
  /** Editor-chosen market. Falls back to FT_1X2 if no odds available. */
  preferredMarket: MarketType;
  isLive?: boolean;
  className?: string;
  /** ISO 8601 kickoff timestamp — passed to addOddsSelection so persisted items expire 3 h after kickoff */
  kickoffAt?: string;
}

/**
 * Maps a `MarketType` code onto the market key expected by `translateOddsSelections`.
 * Codes not in the map pass through unchanged.
 */
const TRANSLATION_MARKET_KEY: Partial<Record<MarketType, string>> = {
  HT_FT: "FIRST_HALF_AND_FINAL_RESULT",
  OVER_GOALS_0_5: "OVER_UNDER",
  OVER_GOALS_1_5: "OVER_UNDER",
  OVER_GOALS_2_5: "OVER_UNDER",
  OVER_GOALS_3_5: "OVER_UNDER",
  OVER_GOALS_4_5: "OVER_UNDER",
  OVER_GOALS_5_5: "OVER_UNDER"
};
function filterOperatorsToMarket(operators: FUSportsBettingOperatorOdds[], marketCode: string): FUSportsBettingOperatorOdds[] {
  return operators.map(op => ({
    ...op,
    markets: op.markets.filter(m => m.type.code === marketCode)
  })).filter(op => op.markets.length > 0);
}

/**
 * Header-card inline odds row with a preferred-market → FT_1X2 fallback.
 * Fetches both markets in one call and renders whichever has data,
 * with a localized market title above the operator odds.
 */
export const HeaderMarketOdds: React.FC<HeaderMarketOddsProps> = t0 => {
  const $ = _c(54);
  const {
    matchId,
    preferredMarket,
    isLive,
    className,
    kickoffAt
  } = t0;
  const labels = useLabels();
  const marketLabels = labels.matchPage.odds.markets;
  const {
    addOddsSelection,
    canAddOdds
  } = useBetslip();
  const tenantOdds = useOddsDisplay();
  let t1;
  if ($[0] !== preferredMarket) {
    t1 = preferredMarket === "FT_1X2" ? ["FT_1X2"] : [preferredMarket, "FT_1X2"];
    $[0] = preferredMarket;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const marketsToFetch = t1;
  let t2;
  if ($[2] !== isLive || $[3] !== marketsToFetch || $[4] !== matchId) {
    t2 = {
      matchId,
      markets: marketsToFetch,
      isLive
    };
    $[2] = isLive;
    $[3] = marketsToFetch;
    $[4] = matchId;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const {
    oddsMap,
    loading
  } = useServerMatchOdds(t2);
  let t3;
  if ($[6] !== labels.matchPage.odds || $[7] !== matchId || $[8] !== oddsMap || $[9] !== preferredMarket) {
    bb0: {
      const raw = oddsMap.get(matchId) || [];
      const build = market => {
        const filtered = filterOperatorsToMarket(raw, market);
        const adapted = filtered.map(toBookmakerOdds).filter(_temp);
        const translationKey = TRANSLATION_MARKET_KEY[market] ?? market;
        return translateOddsSelections(adapted, translationKey, labels.matchPage.odds);
      };
      const preferred = build(preferredMarket);
      if (preferred.length > 0) {
        t3 = {
          effectiveMarket: preferredMarket,
          translated: preferred
        };
        break bb0;
      }
      if (preferredMarket !== "FT_1X2") {
        const fallback = build("FT_1X2");
        if (fallback.length > 0) {
          let t4;
          if ($[11] !== fallback) {
            t4 = {
              effectiveMarket: "FT_1X2" as MarketType,
              translated: fallback
            };
            $[11] = fallback;
            $[12] = t4;
          } else {
            t4 = $[12];
          }
          t3 = t4;
          break bb0;
        }
      }
      let t4;
      if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
        t4 = [];
        $[13] = t4;
      } else {
        t4 = $[13];
      }
      let t5;
      if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
        t5 = {
          effectiveMarket: null as MarketType | null,
          translated: t4 as BookmakerOdds[]
        };
        $[14] = t5;
      } else {
        t5 = $[14];
      }
      t3 = t5;
    }
    $[6] = labels.matchPage.odds;
    $[7] = matchId;
    $[8] = oddsMap;
    $[9] = preferredMarket;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const {
    effectiveMarket,
    translated
  } = t3;
  let t4;
  bb1: {
    if (translated.length !== 1) {
      t4 = null;
      break bb1;
    }
    let t5;
    if ($[15] !== translated[0].odds) {
      t5 = translated[0].odds.find(_temp2)?.url ?? null;
      $[15] = translated[0].odds;
      $[16] = t5;
    } else {
      t5 = $[16];
    }
    t4 = t5;
  }
  const moreOddsUrl = t4;
  let t5;
  bb2: {
    if (!effectiveMarket) {
      t5 = null;
      break bb2;
    }
    switch (effectiveMarket) {
      case "FT_1X2":
        {
          t5 = marketLabels.matchResult;
          break bb2;
        }
      case "DOUBLE_CHANCE":
        {
          t5 = marketLabels.doubleChance;
          break bb2;
        }
      case "BOTH_TEAMS_SCORE":
        {
          t5 = marketLabels.bothTeamsToScore;
          break bb2;
        }
      case "HT_FT":
        {
          t5 = marketLabels.halfTimeFullTime;
          break bb2;
        }
      case "OVER_GOALS_0_5":
      case "OVER_GOALS_1_5":
      case "OVER_GOALS_2_5":
      case "OVER_GOALS_3_5":
      case "OVER_GOALS_4_5":
      case "OVER_GOALS_5_5":
        {
          let t6;
          if ($[17] !== effectiveMarket || $[18] !== marketLabels.overUnderGoalsLine) {
            const line = effectiveMarket.replace("OVER_GOALS_", "").replace("_", ".");
            t6 = marketLabels.overUnderGoalsLine.replace("{line}", line);
            $[17] = effectiveMarket;
            $[18] = marketLabels.overUnderGoalsLine;
            $[19] = t6;
          } else {
            t6 = $[19];
          }
          t5 = t6;
          break bb2;
        }
      default:
        {
          t5 = marketLabels.matchResult;
        }
    }
  }
  const marketTitle = t5;
  const shouldInterceptOdds = canAddOdds && !!effectiveMarket;
  let t6;
  if ($[20] !== addOddsSelection || $[21] !== effectiveMarket || $[22] !== kickoffAt || $[23] !== matchId || $[24] !== shouldInterceptOdds) {
    t6 = odd => {
      if (!shouldInterceptOdds || !effectiveMarket) {
        return;
      }
      addOddsSelection(matchId, effectiveMarket as BetslipMarket, odd.label, kickoffAt);
    };
    $[20] = addOddsSelection;
    $[21] = effectiveMarket;
    $[22] = kickoffAt;
    $[23] = matchId;
    $[24] = shouldInterceptOdds;
    $[25] = t6;
  } else {
    t6 = $[25];
  }
  const handleOddsClick = t6;
  let t7;
  bb3: {
    if (!shouldInterceptOdds) {
      t7 = translated;
      break bb3;
    }
    let t8;
    if ($[26] !== handleOddsClick || $[27] !== translated) {
      let t9;
      if ($[29] !== handleOddsClick) {
        t9 = bookmaker => ({
          ...bookmaker,
          odds: bookmaker.odds.map(odd_0 => ({
            ...odd_0,
            onClick: () => handleOddsClick(odd_0),
            url: undefined
          }))
        });
        $[29] = handleOddsClick;
        $[30] = t9;
      } else {
        t9 = $[30];
      }
      t8 = translated.map(t9);
      $[26] = handleOddsClick;
      $[27] = translated;
      $[28] = t8;
    } else {
      t8 = $[28];
    }
    t7 = t8;
  }
  const processedBookmakerOddsList = t7;
  if (loading) {
    const t8 = `flex gap-2 animate-pulse ${className ?? ""}`;
    let t10;
    let t11;
    let t9;
    if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = <div className="flex-1 h-8 bg-muted rounded" />;
      t10 = <div className="flex-1 h-8 bg-muted rounded" />;
      t11 = <div className="flex-1 h-8 bg-muted rounded" />;
      $[31] = t10;
      $[32] = t11;
      $[33] = t9;
    } else {
      t10 = $[31];
      t11 = $[32];
      t9 = $[33];
    }
    let t12;
    if ($[34] !== t8) {
      t12 = <div className={t8}>{t9}{t10}{t11}</div>;
      $[34] = t8;
      $[35] = t12;
    } else {
      t12 = $[35];
    }
    return t12;
  }
  if (!effectiveMarket || processedBookmakerOddsList.length === 0) {
    return null;
  }
  let t8;
  if ($[36] !== tenantOdds.displayMode || $[37] !== tenantOdds.flashInterval) {
    t8 = {
      container: "p-0",
      operatorContainer: "p-0",
      oddsContainer: "px-2 py-1.5 odds-button",
      mode: tenantOdds.displayMode,
      flashInterval: tenantOdds.flashInterval
    };
    $[36] = tenantOdds.displayMode;
    $[37] = tenantOdds.flashInterval;
    $[38] = t8;
  } else {
    t8 = $[38];
  }
  const mergedStyles = t8;
  let t9;
  if ($[39] !== labels.matchPage.odds.moreOdds || $[40] !== marketTitle || $[41] !== moreOddsUrl) {
    t9 = (marketTitle || moreOddsUrl) && <div className="flex items-center justify-between mb-2">{marketTitle ? <div className="text-xs uppercase tracking-wide font-semibold">{marketTitle}</div> : <span />}{moreOddsUrl && <a href={moreOddsUrl} target="_blank" rel="noopener sponsored" className="text-xs uppercase tracking-wide font-normal opacity-70 hover:opacity-100 hover:underline" onClick={_temp3}>{labels.matchPage.odds.moreOdds} →</a>}</div>;
    $[39] = labels.matchPage.odds.moreOdds;
    $[40] = marketTitle;
    $[41] = moreOddsUrl;
    $[42] = t9;
  } else {
    t9 = $[42];
  }
  let t10;
  if ($[43] !== mergedStyles || $[44] !== processedBookmakerOddsList) {
    let t11;
    if ($[46] !== mergedStyles) {
      t11 = (bookmakerOdds, index) => <BettingOddsUI key={`${bookmakerOdds.operator.name}-${index}`} odds={bookmakerOdds.odds} operator={bookmakerOdds.operator} styles={mergedStyles} />;
      $[46] = mergedStyles;
      $[47] = t11;
    } else {
      t11 = $[47];
    }
    t10 = processedBookmakerOddsList.map(t11);
    $[43] = mergedStyles;
    $[44] = processedBookmakerOddsList;
    $[45] = t10;
  } else {
    t10 = $[45];
  }
  let t11;
  if ($[48] !== t10) {
    t11 = <div className="space-y-3">{t10}</div>;
    $[48] = t10;
    $[49] = t11;
  } else {
    t11 = $[49];
  }
  let t12;
  if ($[50] !== className || $[51] !== t11 || $[52] !== t9) {
    t12 = <div className={className}>{t9}{t11}</div>;
    $[50] = className;
    $[51] = t11;
    $[52] = t9;
    $[53] = t12;
  } else {
    t12 = $[53];
  }
  return t12;
};
function _temp(b) {
  return b !== null;
}
function _temp2(o) {
  return o.url;
}
function _temp3(e) {
  return e.stopPropagation();
}